<template>
  <VModalWidget
    :dismiss="dismiss"
    :submit="doSubmit"
    :submitting="submitting"
    :submitTitle="this.buttonTitle"
    content-class="pt-1"
  >
    <template slot="title">{{ title }}</template>

    <template slot="content">
      <FormErrors :show="showResourceErrors" :errors="resourceErrors" />
      <v-form>
        <v-text-field
          v-model="form.display"
          :label="$t('displayName')"
          :error-messages="displayErrors"
          required
          @input="$v.form.display.$touch()"
          @blur="$v.form.display.$touch()"
          v-on:keyup.enter="doSubmit"
        />
        <v-select
          v-model="form.type"
          :items="actionTypes"
          :label="$t('type')"
          item-text="display"
          item-value="type"
          :error-messages="typeErrors"
          required
          @blur="$v.form.type.$touch()"
        />
        <SendMessageExtension v-if="isNotifyUserAction" :item="item" :updateData="updateData" :setError="setError" />
        <SelectTransitions
          v-if="item"
          :selection="batchTransitions"
          :updateSelection="
            (v) => {
              batchTransitions = v
            }
          "
        />
      </v-form>
    </template>
  </VModalWidget>
</template>

<script>
import { pick } from 'lodash/object'
import { isEmpty } from 'lodash/lang'
import { differenceBy } from 'lodash/array'
import { includes } from 'lodash/collection'
import { mapState, mapActions } from 'vuex'
import { required } from 'vuelidate/lib/validators'
import { BaseForm, FormErrors, VModalWidget } from '@argon/app/components'
import SendMessageExtension from './SendMessageExtension'
import SelectTransitions from './SelectTransitions'
import { makeTransitionList } from '../../utils.js'
import { NAMESPACE } from '../store'

export default {
  name: 'FlowActionCard',
  extends: BaseForm,
  components: {
    VModalWidget,
    FormErrors,
    SendMessageExtension,
    SelectTransitions
  },
  validations: {
    form: {
      display: { required },
      type: { required }
    }
  },
  props: {
    item: Object,
    flowId: {
      type: [String, Number],
      required: true
    },
    dismiss: { type: Function, required: true }
  },
  data: () => ({
    form: {
      display: '',
      type: null
    },
    formData: null,
    extensionError: false,
    batchTransitions: [],
    origTransitions: [],
    isEdit: false,
    submitting: false
  }),
  computed: {
    ...mapState(NAMESPACE, ['current', 'actionTypes']),
    isNotifyUserAction() {
      return this.form.type ? this.form.type.includes('notify_user') : false
    },
    title() {
      return this.item ? this.$t('editTitle') : this.$t('newTitle')
    },
    buttonTitle() {
      return this.isEdit ? this.$t('save') : this.$t('create')
    },
    transitions() {
      return this.current.meta.transitions
    },
    displayErrors() {
      const errors = []
      if (!this.$v.form.display.$dirty) return errors
      !this.$v.form.display.required && errors.push(this.$t('required'))
      this.getServerErrors('display', errors)
      return errors
    },
    typeErrors() {
      const errors = []
      if (!this.$v.form.type.$dirty) return errors
      !this.$v.form.type.required && errors.push(this.$t('required'))
      this.getServerErrors('type', errors)
      return errors
    }
  },
  methods: {
    ...mapActions(NAMESPACE, ['createAction', 'updateAction', 'deleteAction', 'batchUpdateTransitions']),
    setError(v) {
      this.extensionError = v
    },
    updateData(value) {
      this.formData = value
    },
    updateTransitions(created) {
      let addedTransitions = differenceBy(this.batchTransitions, this.origTransitions, 'name')
      let removedTransitions = differenceBy(this.origTransitions, this.batchTransitions, 'name')
      // batch apply this to all selected transitions
      let transitions = created ? makeTransitionList(addedTransitions, removedTransitions, 'actions', created.name) : []
      let updated = { flowId: this.flowId, transitions }
      return this.batchUpdateTransitions(updated)
    },
    doSubmit() {
      this.resetServerErrors()
      if (this.$v.form.$invalid || this.extensionError) {
        return this.$v.form.$touch()
      }
      this.submitting = true
      let callback = this.item ? this.updateAction : this.createAction
      if (!isEmpty(this.formData)) {
        this.form = { ...this.form, data: this.formData }
      } else {
        this.form = { ...this.form, data: {} }
      }
      let data = pick(this.form, ['display', 'type', 'data'])
      if (this.item) {
        data.name = this.item.name
      }
      let payload = { flowId: this.flowId, data }
      callback(payload)
        .then((created) => {
          if (this.item) {
            this.updateTransitions(created).then(() => {
              this.dismiss()
            })
          } else {
            this.dismiss()
          }
        })
        .catch(this.setServerErrors)
        .finally(() => {
          this.submitting = false
        })
    }
  },
  created() {
    if (this.item) {
      this.form = { ...this.item }
      this.batchTransitions = this.transitions.filter((transition) => includes(transition.actions, this.item.name))
      this.origTransitions = this.batchTransitions
      this.isEdit = true
    }
  }
}
</script>

<i18n>
{
  "en": {
    "create": "@:labels.create",
    "displayName": "@:labels.displayName",
    "editTitle": "Edit Action",
    "newTitle": "New Action",
    "required": "@:labels.required",
    "save": "@:labels.save",
    "type": "Type"
  },
  "fr": {
    "create": "@:labels.create",
    "displayName": "@:labels.displayName",
    "editTitle": "Modifier l'action",
    "newTitle": "Nouvelle action",
    "required": "@:labels.required",
    "save": "@:labels.save",
    "type": "Type"
  }
}
</i18n>
